import type { CSSProperties } from 'react';
import Box from '@mui/material/Box';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';
import { TableColumn } from '@backstage/core-components';

/**
 * Columns are matched on their data path, which is part of the row model, not
 * on the (translatable) column title.
 */
const NAME = 'resolved.entityRef';
const DESCRIPTION = 'entity.metadata.description';

/**
 * Left to the table's auto layout, single-token values wrap at every hyphen
 * ("team-|digistore"), so keep those on one line. Owners are capped (and
 * clipped): group refs like org-eu-it-it-ops-devops would otherwise push the
 * table wider than the page.
 */
const cellStyles: Record<string, CSSProperties> = {
  [NAME]: { minWidth: 220 },
  'resolved.ownedByRelationsTitle': { whiteSpace: 'nowrap', maxWidth: 200 },
  'resolved.partOfSystemRelationTitle': { whiteSpace: 'nowrap' },
  'entity.spec.type': { whiteSpace: 'nowrap' },
  'entity.spec.lifecycle': { whiteSpace: 'nowrap' },
};

const matches = (query: string, ...values: (string | undefined)[]) => {
  const q = query.toLowerCase();
  return values.some(value => value?.toLowerCase().includes(q));
};

/**
 * The description moves out of its own column (where it was squeezed down to
 * "The…") and becomes a muted second line under the name, clamped to two
 * lines with the full text on hover. The table's filter box still finds
 * entities by their description.
 */
const withDescription = (
  name: TableColumn<CatalogTableRow>,
): TableColumn<CatalogTableRow> => ({
  ...name,
  customFilterAndSearch: (query, row) =>
    matches(
      String(query),
      row.resolved.entityRef,
      row.entity.metadata.title,
      row.entity.metadata.name,
      row.entity.metadata.description,
    ),
  render: (row, type) => {
    const description = row.entity.metadata.description;
    return (
      <>
        {name.render?.(row, type)}
        {description && (
          <Box
            title={description}
            sx={{
              mt: 0.5,
              color: 'text.secondary',
              fontSize: 13,
              fontWeight: 400,
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {description}
          </Box>
        )}
      </>
    );
  },
});

/** Backstage's own column set per kind, tidied up for our list. */
export const catalogColumns: CatalogTableColumnsFunc = context => {
  const columns = CatalogTable.defaultColumnsFunc(context);
  // Only fold the description in when the kind's default set has one to fold
  const hasDescription = columns.some(c => c.field === DESCRIPTION);
  return columns
    .filter(column => column.field !== DESCRIPTION)
    .map(column =>
      column.field === NAME && hasDescription
        ? withDescription(column)
        : column,
    )
    .map(column => {
      const style = column.field && cellStyles[column.field];
      return style && !column.cellStyle
        ? { ...column, cellStyle: style }
        : column;
    });
};
